import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  TextField,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Check as CheckIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import type { TenantCompanyOption } from '../../Utils/tenantCompanyHelpers';

const SEARCH_THRESHOLD = 8;

interface TenantCompanyFilterSectionProps {
  tenantCompanyOptions: TenantCompanyOption[];
  selectedTenantCompanies: string[];
  tenantsSelected: boolean;
  totalTenantCount: number;
  onToggleCompany: (companyKey: string) => void;
  onSelectAllTenantCompanies: () => void;
  onClearTenantCompanies: () => void;
}

const TenantCompanyFilterSection: React.FC<TenantCompanyFilterSectionProps> = ({
  tenantCompanyOptions,
  selectedTenantCompanies,
  tenantsSelected,
  totalTenantCount,
  onToggleCompany,
  onSelectAllTenantCompanies,
  onClearTenantCompanies,
}) => {
  const [searchText, setSearchText] = useState('');

  // Auto-expand when Tenants is selected or any company is selected
  const isExpanded = tenantsSelected || selectedTenantCompanies.length > 0;

  // Toggle expand manually (but always expand when selections exist)
  const [manualExpand, setManualExpand] = useState(false);
  const showContent = isExpanded || manualExpand;

  // Filter options by search text
  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return tenantCompanyOptions;
    const q = searchText.toLowerCase();
    return tenantCompanyOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [tenantCompanyOptions, searchText]);

  const showSearch = tenantCompanyOptions.length > SEARCH_THRESHOLD;
  const allSelected = selectedTenantCompanies.length === 0 && tenantsSelected;

  return (
    <Box>
      {/* Section header */}
      <ButtonBase
        onClick={() => setManualExpand((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          py: 0.5,
          mb: 0.5,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: appColors.text.heading,
              fontSize: '0.875rem',
              textAlign: 'left',
            }}
          >
            Tenant Company
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: appColors.text.caption,
              fontSize: '0.72rem',
              display: 'block',
              textAlign: 'left',
            }}
          >
            Narrow tenant badge records by company.
          </Typography>
        </Box>
        {showContent ? (
          <ExpandLessIcon sx={{ fontSize: 20, color: appColors.text.caption }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20, color: appColors.text.caption }} />
        )}
      </ButtonBase>

      <Collapse in={showContent}>
        {/* Search (only when > threshold) */}
        {showSearch && (
          <TextField
            size="small"
            placeholder="Search tenant company"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search tenant company"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: appColors.text.placeholder }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: '100%',
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: appColors.surface.card,
                minHeight: 36,
                fontSize: '0.8rem',
              },
            }}
          />
        )}

        {/* "All tenant companies" row */}
        {!searchText.trim() && (
          <CompanyRow
            label="All tenant companies"
            count={totalTenantCount}
            isActive={allSelected}
            onClick={onSelectAllTenantCompanies}
            ariaLabel={`All tenant companies, ${totalTenantCount} people`}
          />
        )}

        {/* Company rows */}
        {filteredOptions.length === 0 && searchText.trim() ? (
          <Typography
            variant="body2"
            sx={{ color: appColors.text.caption, py: 1.5, textAlign: 'center', fontSize: '0.8rem' }}
          >
            No tenant companies found.
          </Typography>
        ) : (
          filteredOptions.map((opt) => (
            <CompanyRow
              key={opt.key}
              label={opt.label}
              count={opt.count}
              isActive={opt.selected}
              onClick={() => onToggleCompany(opt.key)}
              ariaLabel={`Filter by tenant company ${opt.label}, ${opt.count} ${opt.count === 1 ? 'person' : 'people'}`}
            />
          ))
        )}
      </Collapse>
    </Box>
  );
};

interface CompanyRowProps {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
  ariaLabel: string;
}

const CompanyRow: React.FC<CompanyRowProps> = ({ label, count, isActive, onClick, ariaLabel }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={isActive}
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      px: 1.25,
      py: 0.75,
      minHeight: 40,
      borderRadius: '8px',
      border: `1px solid ${isActive ? appColors.primary.main : 'transparent'}`,
      bgcolor: isActive ? `${appColors.primary.main}0A` : 'transparent',
      cursor: 'pointer',
      transition: 'all 0.12s ease',
      '&:hover': {
        bgcolor: isActive ? `${appColors.primary.main}14` : `${appColors.primary.main}06`,
      },
      '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 1 },
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: isActive ? 600 : 400,
        fontSize: '0.82rem',
        color: isActive ? appColors.primary.main : appColors.text.body,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        fontSize: '0.75rem',
        color: isActive ? appColors.primary.main : appColors.text.caption,
        minWidth: 20,
        textAlign: 'right',
        flexShrink: 0,
        ml: 1,
      }}
    >
      {count}
    </Typography>
    {isActive && (
      <CheckIcon
        sx={{ fontSize: 16, color: appColors.primary.main, flexShrink: 0, ml: 0.5 }}
      />
    )}
  </ButtonBase>
);

export default React.memo(TenantCompanyFilterSection);
